// Everything the loader draws over (or instead of) the app, inside the
// closed shadow root: the app-access control, the trial banner, pills,
// toasts, the coach, the App settings panel and the full-screen screens.
// State comes from the loader's store; actions go to its controller (ctl).
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Toast, Button } from '../ui/components.jsx';
import { Glyph } from '../ui/glyphs.jsx';
import { useStore } from './store.js';
import { AccessRow, AccessTab, FloatingGear } from './access/AccessControl.jsx';
import { TrialBanner, TrialChip } from './trial/TrialBanner.jsx';
import { SafeMode, ConnectScreen, RevokedScreen, OfflineScreen, UnavailableScreen, WaitingScreen, StorageScreen, Opening } from './screens.jsx';
import { coachTitle } from './coach/Coach.jsx';
import { KeepSheet } from '../coding/cards.jsx';
import { ActionSheet, useKeepCountdown } from '../coding/changes.jsx';
import { trialBanner } from '../lib/launch.js';
import { noticeText } from '../lib/privacy.js';
import { installEntry } from '../lib/platform.js';

/** Shows `value` only once it has been set for `ms` (no flash for fast work). */
function useDelayed(value, ms) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!value) { setShown(false); return undefined; }
    const t = setTimeout(() => setShown(true), ms);
    return () => clearTimeout(t);
  }, [!!value]);
  return shown ? value : null;
}

function ScreenFor({ s, ctl }) {
  const sc = s.screen;
  const name = s.app?.name;
  switch (sc.kind) {
    case 'safe': return (
      <SafeMode edition={s.edition} appName={name} owner={s.app?.owner_name || 'the owner'} error={sc.error} hung={sc.hung}
        request={sc.request} cancelOffered={sc.request?.actions.includes('cancel')} busy={s.write.busy} cancelState={s.cancelState}
        onRetry={ctl.retrySafe} onOpenCoding={ctl.openCoding} onTellAgent={ctl.tellAgent} origin={s.origin}
        onCancel={() => ctl.openDialog({ action: 'cancel', request: sc.request })} />
    );
    case 'connect': return <ConnectScreen edition={sc.edition} appName={sc.appName} owner={sc.owner} onConnected={ctl.connected} />;
    case 'revoked': return <RevokedScreen owner={sc.owner} onConnected={ctl.connected} />;
    case 'offline': return <OfflineScreen appName={name} busy={sc.busy} onRetry={ctl.retryBoot} />;
    case 'unavailable': return <UnavailableScreen appName={name} busy={sc.busy} onRetry={ctl.retryBoot} />;
    case 'waiting': return <WaitingScreen appName={name} />;
    case 'storage': return <StorageScreen appName={name} busy={sc.busy} onRetry={ctl.retryBoot} />;
    default: return null;
  }
}

/** The launch-report notice: no report leaves this origin until it is acknowledged. */
function PrivacyCard({ s, ctl }) {
  return (
    <div class="mt-privacy-card" role="region" aria-label="About launch reports">
      <span class="mt-privacy-badge"><Glyph name="visibility" /></span>
      <p class="mt-body-s">{noticeText(s.app?.owner_name)}</p>
      <div class="mt-actions">
        <Button kind="text" onClick={ctl.privacyDetails}>Details</Button>
        <Button kind="tonal" onClick={ctl.acceptPrivacy}>OK</Button>
      </div>
    </div>
  );
}

/** The coach's nudge card in the dock. */
function CoachCard({ s, ctl }) {
  const p = s.platform;
  const prompt = p.coach === 'prompt' && s.canInstall;
  return (
    <div class="mt-coach-card" role="region" aria-label="Add to home screen">
      <span class="mt-coach-badge"><Glyph name="install" /></span>
      <p class="mt-title-s">{coachTitle(p, s.app?.name)}</p>
      <p class="mt-body-s mt-muted">{p.coach === 'ios' ? 'Open it full screen, like an app, and get notifications.' : 'Open it full screen, like an app.'}</p>
      <div class="mt-actions">
        <Button kind="text" onClick={ctl.dismissCoach}>Not now</Button>
        {prompt
          ? <Button kind="tonal" onClick={ctl.install}>Install</Button>
          : <Button kind="tonal" onClick={() => ctl.openPanel('coach')}>Show me how</Button>}
      </div>
    </div>
  );
}

export function Shell({ store, ctl }) {
  const s = useStore(store);
  const top = useRef(null);
  const bottom = useRef(null);
  const [edges, setEdges] = useState({ top: 0, bottom: 0 });
  const [Panel, setPanel] = useState(null); // lazily loaded panel module
  const countdown = useKeepCountdown((request) => ctl.sendAction(request, 'keep'));
  const dialog = s.dialog;

  // Publish the rows' space so the app's frame sits between them.
  useLayoutEffect(() => {
    const measure = () => {
      const next = { top: top.current?.offsetHeight ?? 0, bottom: bottom.current?.offsetHeight ?? 0 };
      setEdges((e) => (e.top === next.top && e.bottom === next.bottom ? e : next));
      ctl.setInsets(next);
    };
    measure();
    if (typeof ResizeObserver !== 'function') return undefined;
    const ro = new ResizeObserver(measure);
    if (top.current) ro.observe(top.current);
    if (bottom.current) ro.observe(bottom.current);
    return () => ro.disconnect();
  });

  // Panels load on first use (§9.10: a missing old-generation chunk asks for a reload).
  useEffect(() => {
    if (!s.panel || (Panel && Panel.name === s.panel)) return;
    let live = true;
    ctl.loadPanel(s.panel).then((mod) => { if (live && mod) setPanel({ name: s.panel, mod }); });
    return () => { live = false; };
  }, [s.panel]);

  const opening = useDelayed(s.opening, 350);
  if (s.screen) {
    return (
      <div class="mt-root mt-shell">
        <ScreenFor s={s} ctl={ctl} />
        {dialog && <ActionSheet dialog={dialog} max={s.app?.max_text_chars ?? 2000} busy={s.write.busy} onClose={ctl.closeDialog}
          onConfirm={(text) => ctl.sendAction(dialog.request, dialog.action, text)} />}
      </div>
    );
  }
  if (opening) return <div class="mt-root mt-shell"><Opening appName={s.app?.name} progress={s.opening.progress} /></div>;

  const settings = s.access?.settings;
  const locked = s.access?.defaults.locked;
  // A creator-kept style with nothing else to set has no gear: the row's one
  // button opens Updates.
  const settingsLabel = 'App settings';
  const openSettings = locked && !s.calendar?.relevant ? null : () => { ctl.touch(); ctl.openPanel('access'); };
  const openDest = () => { ctl.touch(); ctl.openCoding(); };
  const fold = (collapsed) => { ctl.touch(); ctl.saveAccess({ ...settings, collapsed }); };
  // Install, next to the way into Coding: Chrome's prompt, or the iOS steps (the same coach panel).
  const install = installEntry(s.platform, s.canInstall);
  const onInstall = () => { ctl.touch(); if (install?.kind === 'prompt') ctl.install(); else ctl.openPanel('coach'); };
  const banner = s.running != null ? trialBanner({ device: s.device, app: s.app, page: s.page, running: s.running, owner: s.app?.owner_name }) : null;
  const layout = settings?.layout;
  const topRow = settings && layout === 'TOP_ROW' && !settings.collapsed;
  const bottomRow = settings && layout === 'BOTTOM_ROW' && !settings.collapsed;
  const collapsedBanner = banner && s.bannerCollapsed;
  const panel = Panel && Panel.name === s.panel ? Panel.mod : null;
  const pills = s.pills;

  return (
    <div class="mt-root mt-shell">
      <div class="mt-shell-top" ref={top}>
        {topRow && (
          <AccessRow edge="top" edition={s.edition} onOpen={openDest} onSettings={openSettings} onFold={() => fold(true)}
            install={install} onInstall={onInstall} settingsLabel={settingsLabel} />
        )}
        {banner && !s.bannerCollapsed && (
          <TrialBanner banner={banner} standalone={!topRow} busy={s.write.busy || !!countdown.keep} error={s.write.error}
            onCollapse={() => ctl.setBannerCollapsed(true)} onOpenCoding={openDest}
            onRetryWrite={ctl.retryWrite} onDismissWrite={ctl.dismissWrite}
            onAction={(a) => {
              ctl.touch();
              if (a === 'keep') countdown.start(banner.request);
              else ctl.openDialog({ action: a, request: banner.request });
            }} />
        )}
      </div>
      {settings && layout === 'TOP_ROW' && settings.collapsed && (
        <AccessTab edge="top" edition={s.edition} onExpand={() => fold(false)} />
      )}
      {collapsedBanner && (
        <TrialChip banner={banner} onExpand={() => ctl.setBannerCollapsed(false)}
          top={edges.top > 0 ? `${edges.top + 8}px` : `calc(${layout === 'TOP_ROW' && settings.collapsed ? 52 : 8}px + var(--mt-safe-top))`} />
      )}
      <div class="mt-shell-bottom" ref={bottom}>
        {bottomRow && (
          <AccessRow edge="bottom" edition={s.edition} onOpen={openDest} onSettings={openSettings} onFold={() => fold(true)}
            install={install} onInstall={onInstall} settingsLabel={settingsLabel} />
        )}
      </div>
      {settings && layout === 'BOTTOM_ROW' && settings.collapsed && (
        <AccessTab edge="bottom" edition={s.edition} onExpand={() => fold(false)} />
      )}
      {settings && layout === 'FLOATING' && (
        <FloatingGear settings={settings} edition={s.edition} onOpen={openDest} onSettings={openSettings} onTouch={ctl.touch}
          onMoved={(place) => ctl.saveAccess({ ...settings, ...place })} install={install} onInstall={onInstall} settingsLabel={settingsLabel} />
      )}
      <div class="mt-shell-dock mt-shell-pass" style={`bottom:${edges.bottom}px`}>
        {pills.refresh && (
          <div class="mt-shell-pill" role="status">
            <Button kind="text" onClick={ctl.refreshSW}><Glyph name="refresh" />Refresh to update</Button>
          </div>
        )}
        {pills.disconnected && (
          <div class="mt-shell-pill" role="status">
            <span>This device isn't connected any more.</span>
            <Button kind="text" onClick={ctl.reconnect}>Connect</Button>
          </div>
        )}
        {(pills.switched || pills.stale) && (
          <div class="mt-shell-pill" role="status">
            <span>This app was updated.</span>
            <Button kind="text" onClick={ctl.reload}>Reload</Button>
          </div>
        )}
        {s.privacyDue && <PrivacyCard s={s} ctl={ctl} />}
        {s.welcome && <Toast text={s.welcome} onDismiss={ctl.dismissWelcome} timeout={9000} />}
        {!s.welcome && s.toast && <Toast text={s.toast} onDismiss={ctl.dismissToast} timeout={6000} />}
        {s.coachDue && !s.privacyDue && !s.welcome && !s.toast && s.panel !== 'coach' && <CoachCard s={s} ctl={ctl} />}
      </div>
      {countdown.keep && <KeepSheet seconds={countdown.keep.seconds} onUndo={countdown.undo} onApply={countdown.applyNow} />}
      {dialog && <ActionSheet dialog={dialog} max={s.app?.max_text_chars ?? 2000} busy={s.write.busy} onClose={ctl.closeDialog}
        onConfirm={(text) => ctl.sendAction(dialog.request, dialog.action, text)} />}
      {panel && s.panel === 'access' && s.access && (
        <div class="mt-shell-screen">
          <panel.AppSettings state={s.access} edition={s.edition} origin={s.origin} appName={s.app?.name}
            calendar={s.calendar} onRefreshCalendar={ctl.refreshCalendar} onConnectCalendar={ctl.connectCalendar} onDisconnectCalendar={ctl.disconnectCalendar}
            onChange={ctl.saveAccess} onReset={ctl.resetAccess} onClose={ctl.closePanel} />
        </div>
      )}
      {panel && s.panel === 'coach' && (
        <panel.Coach platform={s.platform} installPrompt={ctl.installPrompt} appName={s.app?.name}
          connectCode={s.connectCode} onClose={ctl.closePanel} />
      )}
    </div>
  );
}
